// ============================================================
// VOXELS - building with cubes.
//
// The map used to be built from a catalogue of shapes, each with its
// cover and opacity written on it. A GM does not want to pick a wall
// from a catalogue; they want to build *that* building. So authoring is
// now stacked cubes, and everything a cube does is either a property of
// what it is MADE OF or a consequence of how it was BUILT:
//
//   MATERIAL says   hardness (→ cover AP) and transparency (→ sight)
//   GEOMETRY says   cover level (→ how much of the target it hides,
//                   worked out per shot) and passability (→ height)
//
// There is nothing left to assign, and so nothing that can disagree
// with itself. Two walls of the same stuff and the same height cannot
// grant different cover, because neither of them is asked.
//
// GREEDY MESHING is what makes this affordable, and it is also the
// ADAPTER: `lineOfSight`, `movement` and `scene` all consume `Solid[]`
// and always have, so none of them changes at all. A 10×8×4 building is
// 320 cubes and 1,920 faces as voxels; meshed, it is ONE box and six.
//
// Pure. Zero React dependency.
// ============================================================
import type { Solid, TerrainKind } from './types';
import { makeSolid } from './solids';

export interface Material {
  key: string;
  /** The GM's own label. Nothing reads it but a person. */
  name: string;
  colour: string;
  /** 1–10. Becomes the cover AP. */
  hardness: number;
  /**
   * 0 opaque · 1 translucent · 2 transparent.
   *
   * Translucent does not block sight but grants full cover whatever its
   * height: you can see through smoke and you cannot aim through it.
   */
  transparency: 0 | 1 | 2;
  /**
   * 0 solid · 1 dense · 2 open - WHAT YOU CAN WALK THROUGH.
   *
   * The mirror image of `transparency`, and deliberately independent of
   * it: sight and passage are different questions about the same stuff.
   * Smoke you can see nothing through and walk straight into; glass is
   * the other way round.
   *
   *   0 solid   steel, stone, wood, glass - a wall
   *   1 dense   forest, thicket, deep water - you get through, at DOUBLE
   *             cost, which is the game's existing difficult-going rule
   *             and not a new number
   *   2 open    smoke, light, air, a force curtain - no cost at all
   *
   * Absent means solid, which is what every material and every saved map
   * written before v7.26.0 relies on.
   */
  permeability?: 0 | 1 | 2;
  /**
   * Players never see it, and it never reaches them.
   *
   * A property of the STUFF rather than of one wall, so an ambush is
   * built out of "hidden stone" the way a window is built out of
   * glass. `useBattlefieldSync` splits on the flag and writes these
   * to `battlefieldGm/`, which the rules keep players out of - the flag
   * is routing, not just rendering.
   */
  hidden?: boolean;
}

export interface VoxelGrid {
  /** Metres per cube. Matches the map's tile size. */
  size: number;
  /** Sparse. Key is "x,y,z" with y SIGNED, so digging needs no new model. */
  cells: Record<string, string>;
  /**
   * Cells EXCAVATED from the implicitly-solid earth below y = 0.
   *
   * Absence, not presence: recording every cubic metre of a planet would
   * be absurd, so what is recorded is what has been taken away. See
   * `excavation.ts`. The value is the hole's lining: `true` for earth, else a material key (plan map-display, F).
   */
  dug?: Record<string, true | string>;
  materials: Record<string, Material>;
}

/**
 * Shipped materials - DATA, not a hard-coded list. The editor can change
 * every number and add new ones; these exist so nobody has to think
 * before they can build.
 */
/**
 * Plain materials, named for what they ARE.
 *
 * Setting-flavoured names (rockcrete, plasteel, armaglass) told a GM
 * nothing they could not work out from "stone" and cost them a moment's
 * translation every time. The palette is editable anyway: rename Stone to
 * Rockcrete in one click if that is your table's word for it.
 */
export const DEFAULT_MATERIALS: Record<string, Material> = {
  stone: { key: 'stone', name: 'Stone', colour: '#8a8f98', hardness: 8, transparency: 0 },
  steel: { key: 'steel', name: 'Steel', colour: '#6b7d8f', hardness: 10, transparency: 0 },
  wood: { key: 'wood', name: 'Wood', colour: '#9c7247', hardness: 4, transparency: 0 },
  earth: { key: 'earth', name: 'Earth', colour: '#6b5a45', hardness: 6, transparency: 0 },
  rubble: { key: 'rubble', name: 'Rubble', colour: '#7d7360', hardness: 2, transparency: 0 },
  mesh: { key: 'mesh', name: 'Mesh', colour: '#5f6b6f', hardness: 2, transparency: 2 },
  glass: { key: 'glass', name: 'Glass', colour: '#7fb6c9', hardness: 4, transparency: 2 },
  // The two that could not exist before permeability. A forest is opaque
  // and slow and you walk into it; smoke you walk through for nothing.
  forest: { key: 'forest', name: 'Forest', colour: '#3f6b43', hardness: 2, transparency: 0, permeability: 1 },
  water: { key: 'water', name: 'Deep water', colour: '#2f6f8f', hardness: 1, transparency: 2, permeability: 1 },
  smoke: { key: 'smoke', name: 'Smoke', colour: '#b9bec7', hardness: 0, transparency: 1, permeability: 2 },
};

export const DEFAULT_MATERIAL_KEY = 'stone';

export function emptyGrid(size = 1): VoxelGrid {
  return { size, cells: {}, materials: { ...DEFAULT_MATERIALS } };
}

export const cellKey = (x: number, y: number, z: number): string => `${x},${y},${z}`;

export function parseCell(key: string): { x: number; y: number; z: number } {
  const [x, y, z] = key.split(',').map(Number);
  return { x, y, z };
}

/** Set one cell. Returns a NEW grid - the scene memoises on identity. */
export function setCell(grid: VoxelGrid, key: string, material: string): VoxelGrid {
  if (grid.cells[key] === material) return grid;
  return { ...grid, cells: { ...grid.cells, [key]: material } };
}

/** Set many cells in one pass, for a rectangle or a brush stroke. */
export function setCells(grid: VoxelGrid, keys: string[], material: string): VoxelGrid {
  if (!keys.length) return grid;
  const cells = { ...grid.cells };
  for (const key of keys) cells[key] = material;
  return { ...grid, cells };
}

/** Clear one cell. Returns the SAME grid when there was nothing there. */
export function clearCell(grid: VoxelGrid, key: string): VoxelGrid {
  if (!(key in grid.cells)) return grid;
  const cells = { ...grid.cells };
  delete cells[key];
  return { ...grid, cells };
}

export function clearCells(grid: VoxelGrid, keys: string[]): VoxelGrid {
  const present = keys.filter((key) => key in grid.cells);
  if (!present.length) return grid;
  const cells = { ...grid.cells };
  for (const key of present) delete cells[key];
  return { ...grid, cells };
}

export function cellAt(grid: VoxelGrid, x: number, y: number, z: number): string | null {
  return grid.cells[cellKey(x, y, z)] ?? null;
}

/**
 * The level a new cube would land on if dropped here - one above the
 * highest cube at or above the ground. This is what makes stacking work
 * without anyone counting.
 */
export function columnTop(grid: VoxelGrid, x: number, z: number): number {
  let top = 0;
  for (const key of Object.keys(grid.cells)) {
    const c = parseCell(key);
    if (c.x === x && c.z === z && c.y >= 0) top = Math.max(top, c.y + 1);
  }
  return top;
}

/** Hardness is the cover AP, directly. */
export function materialAp(material: Material | undefined): number {
  return material?.hardness ?? 0;
}

/**
 * How a volume of this height, made of this stuff, affects movement.
 *
 * Passability is not something a GM should have to state - a metre of
 * stone stops you whatever anyone says, and a metre of smoke does not.
 * Height decides it for solid stuff; what it is MADE OF decides it for
 * everything else, and the two together are the whole cost model. There
 * is no separate number to balance: `dense` is the game's existing
 * difficult-going, which already costs double.
 *
 * (The height rule is also the fix for a v7.16.0 bug: every brush except
 * the chasm was left `terrain: 'normal'`, so the movement preview walked
 * straight through walls.)
 */
export function derivePassability(height: number, permeability: 0 | 1 | 2 = 0): TerrainKind {
  if (permeability === 2) return 'normal';
  if (permeability === 1) return 'difficult';
  // HEIGHT IS THE CLIMB RULE'S BUSINESS, not this one's (v7.97.0).
  //
  // Calling anything a metre tall `impassable` was a stand-in for a
  // vertical rule the flood fill did not have. It did stop people
  // walking through walls, and it also made the roof of every building
  // unwalkable, every wall unclimbable at any price, and `climbCost` a
  // charge that only ever applied to kerbs. `arrivalSurface` answers the
  // question properly now: a wall in your path is climbed over, and what
  // stops you is the movement budget.
  //
  // An author who wants an absolute barrier still has one - `terrain`
  // set to `impassable` by hand, which nothing derives and nothing
  // overrides.
  // LOW BROKEN GROUND IS ROUGH; A STRUCTURE HAS A FLAT TOP. Rubble and
  // kerbs are scrambled over, and that is what this rung is for. Once a
  // thing is tall enough to be a building its top is a floor and its
  // side is a climb, so calling the whole column rough would make every
  // roof cost double to walk across.
  if (height >= 0.3 && height < 1) return 'difficult';
  return 'normal';
}

/** A run of cells meshed into one box: origin and extent, in cells. */
export interface CellBox {
  x: number;
  y: number;
  z: number;
  w: number;
  h: number;
  d: number;
}

/** A box, and what every cell in it is. */
interface KindedBox extends CellBox {
  kind: string;
}

type Cell = { x: number; y: number; z: number };
type IsFree = (x: number, y: number, z: number) => boolean;

/**
 * Greedy box meshing: grow along x, then z, then y, while the whole
 * slab matches. The standard voxel technique, and the single reason a
 * building is affordable to draw.
 *
 * Generic over what a cell IS: `kindOf` names it (a material for the
 * voxels, one constant for the earth around a trench) and answers
 * undefined for an empty cell. Cells only join a box of their own kind.
 * Excavation meshes its earth shell through this same pass.
 */
export function greedyMesh(keys: Iterable<string>, kindOf: (key: string) => string | undefined): KindedBox[] {
  const taken = new Set<string>();
  const boxes: KindedBox[] = [];
  for (const key of sortedCells(keys)) {
    if (taken.has(key)) continue;
    const kind = kindOf(key) as string;
    const free: IsFree = (x, y, z) => {
      const k = cellKey(x, y, z);
      return kindOf(k) === kind && !taken.has(k);
    };
    const box = growBox(parseCell(key), free);
    claim(box, taken);
    boxes.push({ ...box, kind });
  }
  return boxes;
}

/** Sorted by y, then z, then x, so the result is deterministic - the renderer keys off it. */
function sortedCells(keys: Iterable<string>): string[] {
  return [...keys].sort((a, b) => {
    const p = parseCell(a);
    const q = parseCell(b);
    return p.y - q.y || p.z - q.z || p.x - q.x;
  });
}

/** The biggest box from `origin`: as wide as the row runs, then as deep, then as tall. */
function growBox(origin: Cell, free: IsFree): CellBox {
  const { x, y, z } = origin;
  let w = 1;
  while (free(x + w, y, z)) w++;
  let d = 1;
  while (rowFree(free, { x, y, z: z + d }, w)) d++;
  let h = 1;
  while (slabFree(free, { x, y: y + h, z }, w, d)) h++;
  return { x, y, z, w, h, d };
}

/** Are the `w` cells running along x from `at` all free? */
function rowFree(free: IsFree, at: Cell, w: number): boolean {
  for (let i = 0; i < w; i++) if (!free(at.x + i, at.y, at.z)) return false;
  return true;
}

/** Is the whole `w × d` slab at `at`'s height free? */
function slabFree(free: IsFree, at: Cell, w: number, d: number): boolean {
  for (let j = 0; j < d; j++) if (!rowFree(free, { x: at.x, y: at.y, z: at.z + j }, w)) return false;
  return true;
}

/** Mark every cell of a box as used. */
function claim(box: CellBox, taken: Set<string>): void {
  for (let i = 0; i < box.w; i++) {
    for (let j = 0; j < box.d; j++) {
      for (let k = 0; k < box.h; k++) taken.add(cellKey(box.x + i, box.y + k, box.z + j));
    }
  }
}

/**
 * The shape of a meshed box, in metres.
 *
 * The apothem carries the grid SIZE and the scale carries the cell
 * COUNT, so a box is `w × size` metres wide. Leaving the size out here
 * makes every map render at one metre per cell whatever its tile size
 * says.
 */
export function boxShape(box: CellBox, size: number) {
  return {
    sides: 4,
    radiusBottom: 0.5 * size,
    radiusTop: 0.5 * size,
    scaleX: box.w,
    scaleZ: box.d,
    height: box.h * size,
    position: {
      x: (box.x + box.w / 2) * size,
      y: box.y * size,
      z: (box.z + box.d / 2) * size,
    },
  };
}

/**
 * Memoised on grid IDENTITY, matching the `projectDefender` precedent -
 * and honouring the same hard rule: never mutate a grid in place, or
 * this cache makes the change invisible to every panel.
 */
const meshCache = new WeakMap<VoxelGrid, Solid[]>();

/** Turn a voxel grid into the solids the rest of the engine already speaks. */
export function meshVoxels(grid: VoxelGrid): Solid[] {
  const cached = meshCache.get(grid);
  if (cached) return cached;

  const size = grid.size > 0 ? grid.size : 1;
  const solids = greedyMesh(Object.keys(grid.cells), (key) => grid.cells[key]).map((box) =>
    voxelSolid(box, grid.materials?.[box.kind] ?? DEFAULT_MATERIALS[box.kind], size),
  );

  meshCache.set(grid, solids);
  return solids;
}

/**
 * One meshed box as a solid, everything it does read off its material.
 *
 * A STABLE ID, DERIVED FROM THE BOX ITSELF. `makeSolid` mints one from a
 * counter, so every re-mesh renamed every solid - and a mesh is
 * regenerated on each brush stroke. React keys faces by solid id, so the
 * whole map was torn down and rebuilt on every stroke: the arrival
 * animation played over the entire arena instead of over what had just
 * been built, and every polygon churned for nothing (v7.97.0).
 *
 * A cell can belong to one box only, so origin plus extent plus material
 * is unique within a grid - and it changes exactly when the box does,
 * which is when it SHOULD count as new terrain.
 */
function voxelSolid(box: KindedBox, material: Material | undefined, size: number): Solid {
  const transparency = material?.transparency ?? 0;
  return makeSolid({
    id: `vox-${box.x},${box.y},${box.z}-${box.w}x${box.h}x${box.d}-${box.kind}`,
    ...boxShape(box, size),
    // Hardness sets the AP; the LEVEL is worked out per shot, because
    // it depends on who is being shot at.
    cover: { level: 'none', ap: materialAp(material) },
    coverMode: 'derived',
    transparency,
    opaque: transparency === 0,
    ...(material?.colour ? { colour: material.colour } : {}),
    terrain: derivePassability(box.h * size, material?.permeability ?? 0),
    ...optionalMaterialFields(material),
  });
}

/** The material facts a solid carries only when they are set. */
function optionalMaterialFields(material: Material | undefined): Partial<Solid> {
  return {
    ...(material?.permeability ? { permeability: material.permeability } : {}),
    ...(material?.hidden ? { hidden: true } : {}),
    ...(material?.name ? { label: material.name } : {}),
  };
}
